import { GuidToString, Keys, RecordMerge } from "@pdfking/tfunc"

import type { PdfviewState } from "#public"
import type { DroppedFiles } from "#public"

import { NewId } from "./Counters"

// The document grown by a set of dropped files: the files added, and one
// file-key row minted per name that wasn't already there. Rows deliberately
// carry no `file_range` — a page count only exists once the file has been
// split, and the viewer's own `fill_range` writes it in as each split lands
// (the same shape the desktop host builds for "Open File…", see main.rs).
//
// The fresh `revision` is what makes the viewer *apply* this instead of
// skipping it: it recognizes the echo of its own pushes by comparing the
// incoming revision against the last one it applied, so an inbound document
// change has to carry one it has never seen.
//
// What a drop *means* is the same wherever the viewer is embedded, so this is
// the shared other half of {@link DropArea}: the host catches the gesture and
// pushes this patch onto whatever route it keeps the state in.
// Takes only the two fields it grows, so a host seeding a viewer from nothing
// (see welcome.tsx's `DroppedState`) applies it to an empty state instead of
// keeping a from-scratch copy of the row minting.
export const WithDroppedFiles = (
  state: Pick<PdfviewState, "files" | "imposition_data">,
  dropped: DroppedFiles
): Pick<PdfviewState, "files" | "imposition_data" | "revision"> => {
  const rows = (state.imposition_data["file_key"] as Record<string, unknown>[] | undefined) ?? []
  // A row per name no row places yet: a name a row already points at keeps
  // its row (the new file replaces the old under it), and a file whose row was
  // deleted gets one back.
  const placed = new Set(rows.map(row => row.file_id))
  const added = Keys(dropped).filter(name => !placed.has(name))
  return {
    files: RecordMerge(state.files, dropped),
    imposition_data: {
      ...state.imposition_data,
      file_key: [...rows, ...added.map(name => ({ file_id: name }))],
    },
    revision: GuidToString(NewId()),
  }
}
